import { useMemo, useState } from 'react'
import type { NodeSummary } from '@/types'


const UNASSIGNED_COMPANY = 'Unassigned'
const NO_SITE = 'No site/area'

export function nodeCompany(node: NodeSummary): string {
  return node.company?.trim() || UNASSIGNED_COMPANY
}

export function nodeSite(node: NodeSummary): string {
  return node.site?.trim() || NO_SITE
}

export function nodeDisplayName(node: NodeSummary): string {
  return node.name?.trim() || node.node_key
}

export function formatNodeOption(node: NodeSummary): string {
  return `${nodeDisplayName(node)} — ${nodeCompany(node)} / ${nodeSite(node)} (${node.status})`
}

interface Props {
  nodes: NodeSummary[]
  nodesLoading: boolean
  selectedNodeId: string
  onSelectNode: (nodeId: string) => void
  ariaLabelPrefix: string
}

export default function NodeHierarchyPicker({
  nodes,
  nodesLoading,
  selectedNodeId,
  onSelectNode,
  ariaLabelPrefix,
}: Props) {
  const [companyFilter, setCompanyFilter] = useState('')
  const [siteFilter, setSiteFilter] = useState('')

  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) ?? null,
    [nodes, selectedNodeId],
  )

  const companies = useMemo(
    () => Array.from(new Set(nodes.map(nodeCompany))).sort((a, b) => a.localeCompare(b)),
    [nodes],
  )

  const sites = useMemo(
    () => Array.from(new Set(
      nodes
        .filter((node) => !companyFilter || nodeCompany(node) === companyFilter)
        .map(nodeSite),
    )).sort((a, b) => a.localeCompare(b)),
    [companyFilter, nodes],
  )

  const filteredNodes = useMemo(
    () => nodes
      .filter((node) => (
        (!companyFilter || nodeCompany(node) === companyFilter)
        && (!siteFilter || nodeSite(node) === siteFilter)
      ))
      .sort((a, b) => nodeDisplayName(a).localeCompare(nodeDisplayName(b))),
    [companyFilter, nodes, siteFilter],
  )

  const selectedNodeOutsideFilters = Boolean(
    selectedNode && !filteredNodes.some((node) => node.id === selectedNode.id),
  )

  return (
    <div style={pickerGridStyle}>
      <label style={labelStyle}>
        <span>Customer</span>
        <select
          value={companyFilter}
          onChange={(event) => {
            setCompanyFilter(event.target.value)
            setSiteFilter('')
          }}
          style={selectStyle}
          disabled={nodesLoading}
          aria-label={`${ariaLabelPrefix} customer`}
        >
          <option value="">All customers</option>
          {companies.map((company) => (
            <option key={company} value={company}>{company}</option>
          ))}
        </select>
      </label>

      <label style={labelStyle}>
        <span>Site / area</span>
        <select
          value={siteFilter}
          onChange={(event) => setSiteFilter(event.target.value)}
          style={selectStyle}
          disabled={nodesLoading}
          aria-label={`${ariaLabelPrefix} site`}
        >
          <option value="">All sites/areas</option>
          {sites.map((site) => (
            <option key={site} value={site}>{site}</option>
          ))}
        </select>
      </label>

      <label style={{ ...labelStyle, gridColumn: 'span 2' }}>
        <span>Physical node</span>
        <select
          value={selectedNodeId}
          onChange={(event) => onSelectNode(event.target.value)}
          style={selectStyle}
          disabled={nodesLoading}
          aria-label={`${ariaLabelPrefix} physical node`}
        >
          <option value="">
            {nodesLoading ? 'Loading nodes...' : 'Select physical node...'}
          </option>
          {selectedNodeOutsideFilters && selectedNode && (
            <option value={selectedNode.id}>
              {formatNodeOption(selectedNode)} — outside current filters
            </option>
          )}
          {filteredNodes.length === 0 && !nodesLoading && (
            <option value="" disabled>No nodes match filters</option>
          )}
          {filteredNodes.map((node) => (
            <option key={node.id} value={node.id}>
              {formatNodeOption(node)}
            </option>
          ))}
        </select>
      </label>
    </div>
  )
}

const pickerGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(150px, 1fr))',
  gap: 10,
  marginBottom: 16,
}

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  minWidth: 0,
  color: '#666',
  fontSize: 12,
  fontWeight: 600,
}

const selectStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  border: '1px solid #d0d0d0',
  borderRadius: 4,
  background: '#fff',
  color: '#333',
  fontFamily: 'inherit',
  fontSize: 13,
  cursor: 'pointer',
}
